import { RESTRICTED } from '@boViews/CollectionDetail/accessRules';
import { formatList, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import Action from '@sharedComponents/Action';
import FlexBox from '@sharedComponents/FlexBox';
import Tag from '@sharedComponents/Tag';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { accessSummary, targetLine, targetPath } from '../accessRules';

import * as style from './style.module.css';

/**
 * Cell of the "Clients" column of the collection list (see accessSummary), on a single line: "General" for a
 * public collection, with a `-n` badge counting its hidden targets; the first authorized target of a restricted
 * collection, a client by its name or a platform by its url, linked to its page in the same tab and cut with an
 * ellipsis past a maximum width (its title holds the full text), with a `+n` badge counting the others; "No
 * client", as a warning, for a restricted collection without authorized target. The badge takes the focus and
 * lists every target, one per line, in a tooltip shown on hover and on focus, which is also the description of the
 * badge for screen readers.
 */
const ClientsCell = ({ collection }) => {
    const { t, i18n } = useTranslation('bo');
    const descriptionId = useId();
    const { visibility, first, badge } = accessSummary(collection);

    // A single line, however narrow the column gets.
    const cell = (content) => (
        <FlexBox alignItems="center" gap="xs" className={style.cell}>
            {content}
        </FlexBox>
    );

    if (visibility === RESTRICTED && !first) {
        return cell(
            <Typography span fontSize="s" color="warning-primary" className={style.text}>
                {t('No client')}
            </Typography>,
        );
    }

    const firstLabel = first?.platform?.url ?? first?.client?.name;
    const main = first ? (
        <Action action={`${import.meta.env.BASE_URL}${targetPath(first)}`} title={firstLabel} className={style.link}>
            <Typography span fontSize="s" color="neutral-primary" className={`${style.text} ${style.name}`}>
                {firstLabel}
            </Typography>
        </Action>
    ) : (
        <Typography span fontSize="s" color="neutral-primary" className={style.text}>
            {t('General')}
        </Typography>
    );
    if (!badge) return cell(main);

    const lines = (collection.accessTargets ?? []).map((target) => ({
        key: target.platform?.uuid ?? target.client?.uuid,
        text: targetLine(t, target),
    }));
    const label = badge.sign === '-' ? t('{{count}} hidden', { count: badge.count }) : t('{{count}} more', { count: badge.count });
    const tooltip = lines.map((line) => (
        <Typography key={line.key} span fontSize="xs" color="neutral-secondary" className={style.line}>
            {line.text}
        </Typography>
    ));

    return cell(
        <>
            {main}
            <Tooltip text={tooltip} popupIcon={false} position="right">
                <FlexBox role="img" tabIndex={0} aria-label={label} aria-describedby={descriptionId} className={style.trigger}>
                    <Tag color={badge.sign === '-' ? 'warning' : 'lavender'} size="xs" fontSize="xs">
                        {`${badge.sign}${badge.count}`}
                    </Tag>
                    <Typography span hidden id={descriptionId}>
                        {formatList(
                            lines.map((line) => line.text),
                            uiLocale(i18n),
                        )}
                    </Typography>
                </FlexBox>
            </Tooltip>
        </>,
    );
};

export default ClientsCell;
